import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/Images/wstsc-logo-white.png";
import { ORG, VENUE_ADDRESS_LINE } from "../seo/site.js";

const navLinks = [
  { name: "Tamil Classes in Sydney", href: "/tamil-classes-sydney/" },
  { name: "Programs & Levels", href: "/programs/" },
  { name: "How to Enrol", href: "/enrolment/" },
  { name: "About Us", href: "/about/" },
  { name: "Teachers & Volunteers", href: "#team" },
  { name: "Events", href: "/events/" },
  { name: "Contact Us", href: "/contact/" },
  // { name: "Donate", href: "/donate/" },
];

const socialLinks = [
  {
    name: "Facebook",
    icon: "bi bi-facebook",
    href: ORG.social.facebook,
  },
  {
    name: "Instagram",
    icon: "bi bi-instagram",
    href: ORG.social.instagram,
  },
  {
    name: "YouTube",
    icon: "bi bi-youtube",
    href: ORG.social.youtube,
  },
  {
    name: "WhatsApp",
    icon: "bi bi-whatsapp",
    href: ORG.whatsapp,
  },
];

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // 🔹 Handle navigation (normal route + section scroll) - Same as header
  const handleNavClick = (href) => {
    if (href.startsWith("#")) {
      const sectionId = href.substring(1);
      if (location.pathname === "/") {
        // Already home → just scroll
        const section = document.getElementById(sectionId);
        if (section) section.scrollIntoView({ behavior: "smooth" });
      } else {
        // Coming from another page → Home scrolls once its data has loaded
        navigate("/", { state: { scrollTo: sectionId } });
      }
    } else {
      // Normal page navigation (like /coming-soon)
      navigate(href);
    }
  };

  return (
    <footer className="red-bg text-light overflow-hidden">
      <div className="container">
        <div className="row py-5">
          {/* Left Column: Logo, Description, Social Icons */}
          <div className="col-lg-4 col-md-12 mb-4 d-flex flex-column align-items-center align-items-md-start">
            <Link to="/" className="d-inline-block mb-3">
              <img
                src={logo}
                alt="Western Sydney Tamil Study Centre"
                className="bg-transparent p-1 responsive-logo"
              />
            </Link>
            <p className="small text-center text-md-start">
              The Western Sydney Tamil Study Centre (WSTSC) is a community based
              language school committed to teaching and nurturing the Tamil
              language and culture for students from Preschool to Year 12
            </p>

            {/* Mobile Only: Social Media Icons in One Row */}
            <div className="d-md-none d-flex align-items-center gap-3 mt-2">
              {socialLinks.map((social, index) =>
                social.href.startsWith("#") ? (
                  <button
                    key={index}
                    onClick={() => handleNavClick(social.href)}
                    className="text-white border-0 bg-transparent p-0"
                    style={{ cursor: "pointer" }}
                    aria-label={social.name}
                  >
                    <i className={`${social.icon} fs-5`}></i>
                  </button>
                ) : (
                  <a
                    key={index}
                    href={social.href}
                    className="text-white"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                  >
                    <i className={`${social.icon} fs-5`}></i>
                  </a>
                ),
              )}
            </div>
          </div>

          {/* Right Column: 3 Sections in a Row */}
          <div className="col-lg-8 col-md-12 ">
            <div className="row gy-4">
              {/* LINK Section */}
              <div className="col-md-4  d-flex flex-column align-items-center align-items-md-start">
                <h5 className="fw-bold mb-3 text-uppercase">Quick LINKs</h5>
                <ul className="list-unstyled text-center text-md-start">
                  {navLinks.map((link, index) => (
                    <li key={index} className="mb-2">
                      <a
                        href={link.href.startsWith("#") ? `/${link.href}` : link.href}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavClick(link.href);
                        }}
                        className="text-light text-decoration-none"
                      >
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* SUPPORT Section - Hidden on mobile, shown on desktop */}
              <div className="col-md-4 d-none d-md-flex flex-column align-items-center align-items-md-start">
                <h5 className="fw-bold mb-3 text-uppercase">
                  Social Media Links
                </h5>
                <ul className="list-unstyled text-center text-md-start">
                  {socialLinks.map((link, index) => (
                    <li key={index} className="mb-2">
                      {link.href.startsWith("#") ? (
                        // Internal section scroll (keeps path at "/")
                        <button
                          onClick={() => handleNavClick(link.href)}
                          className="text-light text-decoration-none d-flex align-items-center gap-2 border-0 bg-transparent p-0"
                          style={{ cursor: "pointer" }}
                          aria-label={link.name}
                        >
                          <i className={link.icon}></i>
                          {link.name}
                        </button>
                      ) : (
                        // External links open normally
                        <a
                          href={link.href}
                          className="text-light text-decoration-none d-flex align-items-center gap-2"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className={link.icon}></i>
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact Section - name, address and phone match the structured data */}
              <div className="col-md-4 d-flex flex-column align-items-center align-items-md-start">
                <h5 className="fw-bold mb-3 text-uppercase">Visit or Contact</h5>
                <address className="text-center text-md-start mb-0">
                  <p className="mb-2">
                    {ORG.venue.name}
                    <br />
                    {VENUE_ADDRESS_LINE}
                  </p>
                  <p className="mb-1">
                    Committee:{" "}
                    <a href={`tel:${ORG.phones.committee.tel}`} className="text-white">
                      {ORG.phones.committee.display}
                    </a>
                  </p>
                  <p className="mb-1">
                    Principal:{" "}
                    <a href={`tel:${ORG.phones.principal.tel}`} className="text-white">
                      {ORG.phones.principal.display}
                    </a>
                  </p>
                  <p className="mb-0">
                    <a href={`mailto:${ORG.email}`} className="text-white">
                      {ORG.email}
                    </a>
                  </p>
                </address>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="row border-top py-4 text-center small">
          <p className="mb-0">
            {" "}
            All rights reserved. {new Date().getFullYear()} &copy;{" "}
            <strong className="text-white">
              Western Sydney Tamil Study Centre
            </strong>
            <br />
            Web solution by{" "}
            <a
              href="https://www.adada.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white text-decoration-none"
            >
              ADADA Digital
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
